<template>
  <div class="page-container">
    <div class="page-content">
      <div class="form-container">
    <div class="header mb-6">
      <div class="flex justify-between items-center">
        <div>
          <h1 class="text-2xl font-bold text-gray-900">
            {{ isEditing ? t('products.formTitleEdit') : t('products.formTitleNew') }}
          </h1>
          <p class="text-gray-600">
            {{ isEditing ? t('products.formSubtitleEdit') : t('products.formSubtitleNew') }}
          </p>
        </div>
        <div class="flex gap-3">
          <router-link to="/products" class="btn btn-secondary">
            {{ t('common.cancel') }}
          </router-link>
          <button
            @click="handleSubmit"
            :disabled="loading || !isFormValid"
            class="btn btn-primary"
          >
            {{ loading ? t('common.saving') : (isEditing ? t('common.update') : t('common.create')) }}
          </button>
        </div>
      </div>
    </div>

    <!-- Error Alert -->
    <div v-if="error" class="alert alert-error mb-4">
      <span>{{ error }}</span>
      <button @click="clearError" class="btn btn-sm btn-ghost">×</button>
    </div>

    <!-- Form -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <!-- Main Form -->
      <div class="lg:col-span-2">
        <div class="card">
          <div class="card-header">
            <h2 class="text-lg font-semibold">{{ t('products.sectionBasic') }}</h2>
          </div>
          <div class="card-body space-y-4">
            <div class="form-group">
              <label for="name" class="form-label">
                {{ t('products.labelName') }}
              </label>
              <input
                id="name"
                v-model="formData.name"
                type="text"
                required
                class="input"
                :class="{ 'input-error': errors['name']?.length }"
                @blur="validateField('name')"
              />
              <div v-if="errors['name']?.length" class="text-sm text-red-600">
                {{ errors['name'][0] }}
              </div>
            </div>

            <div class="form-group">
              <label for="sku" class="form-label">
                {{ t('products.labelSku') }}
              </label>
              <input
                id="sku"
                v-model="formData.sku"
                type="text"
                required
                class="input"
                :class="{ 'input-error': errors['sku']?.length }"
                @blur="validateField('sku')"
              />
              <div v-if="errors['sku']?.length" class="text-sm text-red-600">
                {{ errors['sku'][0] }}
              </div>
            </div>

            <div class="form-group">
              <label for="description" class="form-label">
                {{ t('products.labelDescription') }}
              </label>
              <textarea
                id="description"
                v-model="formData.description"
                class="textarea"
                :class="{ 'input-error': errors['description']?.length }"
                @blur="validateField('description')"
                rows="3"
              ></textarea>
              <div v-if="errors['description']?.length" class="text-sm text-red-600">
                {{ errors['description'][0] }}
              </div>
            </div>

            <div class="form-group">
              <label for="category" class="form-label">
                {{ t('products.labelCategory') }}
              </label>
              <input
                id="category"
                v-model="formData.category"
                type="text"
                class="input"
                :class="{ 'input-error': errors['category']?.length }"
                @blur="validateField('category')"
              />
              <div v-if="errors['category']?.length" class="text-sm text-red-600">
                {{ errors['category'][0] }}
              </div>
            </div>
          </div>
        </div>

        <!-- Pricing Information -->
        <div class="card mt-6">
          <div class="card-header">
            <h2 class="text-lg font-semibold">{{ t('products.sectionPricing') }}</h2>
          </div>
          <div class="card-body">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="form-group">
                <label class="form-label">{{ t('products.labelCostPrice') }}</label>
                <input
                  v-model.number="formData.cost"
                  type="number"
                  step="0.01"
                  min="0.01"
                  class="input"
                  :class="{ 'input-error': errors['cost']?.length }"
                  placeholder="0.01"
                  @blur="validateField('cost')"
                />
                <div v-if="errors['cost']?.length" class="text-sm text-red-600">
                  {{ errors['cost'][0] }}
                </div>
              </div>
              <div class="form-group">
                <label class="form-label">{{ t('products.labelSellingPrice') }}</label>
                <input
                  v-model.number="formData.price"
                  type="number"
                  step="0.01"
                  min="0.01"
                  class="input"
                  :class="{ 'input-error': errors['price']?.length }"
                  placeholder="0.01"
                  @blur="validateField('price')"
                />
                <div v-if="errors['price']?.length" class="text-sm text-red-600">
                  {{ errors['price'][0] }}
                </div>
              </div>
              <div class="form-group">
                <label class="form-label">{{ t('products.labelMinQuantity') }}</label>
                <input
                  v-model.number="formData.minQuantity"
                  type="number"
                  min="0"
                  class="input"
                  :class="{ 'input-error': errors['minQuantity']?.length }"
                  placeholder="0"
                  @blur="validateField('minQuantity')"
                />
                <div v-if="errors['minQuantity']?.length" class="text-sm text-red-600">
                  {{ errors['minQuantity'][0] }}
                </div>
              </div>
              <div class="form-group">
                <label class="form-label">{{ t('products.labelMaxQuantity') }}</label>
                <input
                  v-model.number="formData.maxQuantity"
                  type="number"
                  min="0"
                  class="input"
                  :class="{ 'input-error': errors['maxQuantity']?.length }"
                  placeholder="1000"
                  @blur="validateField('maxQuantity')"
                />
                <div v-if="errors['maxQuantity']?.length" class="text-sm text-red-600">
                  {{ errors['maxQuantity'][0] }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Sidebar -->
      <div class="space-y-6">
        <!-- Status -->
        <div class="card">
          <div class="card-header">
            <h2 class="text-lg font-semibold">{{ t('products.sectionStatus') }}</h2>
          </div>
          <div class="card-body">
            <div class="flex items-center space-x-3">
              <input
                v-model="formData.isActive"
                type="checkbox"
                id="isActive"
                class="checkbox"
              />
              <label for="isActive" class="text-sm font-medium text-gray-700">
                {{ t('products.labelIsActive') }}
              </label>
            </div>
            <p class="text-xs text-gray-500 mt-2">
              {{ t('products.inactiveHelp') }}
            </p>
          </div>
        </div>

        <!-- Barcode Scanner -->
        <div class="card">
          <div class="card-header">
            <h2 class="text-lg font-semibold">{{ t('products.sectionBarcode') }}</h2>
          </div>
          <div class="card-body">
            <div class="space-y-4">
              <div>
                <label class="form-label">{{ t('products.labelBarcode') }}</label>
                <input
                  v-model="formData.barcode"
                  type="text"
                  class="input"
                  :class="{ 'input-error': errors['barcode']?.length }"
                  :placeholder="t('products.placeholderBarcode')"
                  @blur="validateField('barcode')"
                />
                <div v-if="errors['barcode']?.length" class="text-sm text-red-600">
                  {{ errors['barcode'][0] }}
                </div>
              </div>
              <BarcodeScanner
                @scanned="handleBarcodeScanned"
                @error="handleScannerError"
                class="w-full"
              />
            </div>
          </div>
        </div>

        <!-- Quick Actions -->
        <div class="card">
          <div class="card-header">
            <h2 class="text-lg font-semibold">{{ t('products.sectionQuickActions') }}</h2>
          </div>
          <div class="card-body space-y-2">
            <button @click="generateSKU" class="btn btn-sm btn-ghost w-full">
              {{ t('products.generateSku') }}
            </button>
            <button @click="generateBarcode" class="btn btn-sm btn-ghost w-full">
              {{ t('products.generateBarcode') }}
            </button>
            <button @click="duplicateProduct" v-if="isEditing" class="btn btn-sm btn-ghost w-full">
              {{ t('products.duplicateProduct') }}
            </button>
          </div>
        </div>
      </div>
    </div>
      </div>
    </div>
  </div>
</template>


<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useProductStore } from '../../stores/useProductStore';
// @ts-ignore — packages/ui excluded from tsconfig.app.json
import BarcodeScanner from '../../../packages/ui/src/components/BarcodeScanner.vue';
import { z } from 'zod';

const { t } = useI18n();

interface FieldBase {
  name: string
  label: string
  type: string
  required?: boolean
  validation?: z.ZodType<any>
  props?: Record<string, any>
}

interface TextFieldProps extends FieldBase {
  type: 'text' | 'email' | 'password' | 'number'
  minLength?: number
  maxLength?: number
}

interface SelectFieldProps extends FieldBase {
  type: 'select'
  options: Array<{ value: string | number; label: string }>
}

interface CheckboxFieldProps extends FieldBase {
  type: 'checkbox'
  checked?: boolean
}

type FieldProps = TextFieldProps | SelectFieldProps | CheckboxFieldProps;

const route = useRoute();
const router = useRouter();
const productStore = useProductStore();

const formData = ref({
  name: '',
  sku: '',
  description: '',
  barcode: '',
  category: '',
  cost: undefined as number | undefined,
  price: undefined as number | undefined,
  minQuantity: undefined as number | undefined,
  maxQuantity: undefined as number | undefined,
  isActive: true
});

const productCurrency = ref('USD');
const productUnit = ref('units');
const loading = ref(false);
const error = ref<string | null>(null);
const errors = ref<Record<string, string[]>>({});
const isSubmitting = ref(false);

const isEditing = computed(() => !!route.params.id);
const productId = computed(() => route.params.id as string);
const hasErrors = computed(() =>
  Object.values(errors.value).some(fieldErrors => fieldErrors.length > 0)
);

const formSchema = z.object({
  name: z.string().min(1, 'Product name is required'),
  sku: z.string()
    .min(1, 'SKU is required')
    .regex(/^[A-Za-z0-9-_]+$/, 'SKU can only contain letters, numbers, hyphens and underscores'),
  description: z.string().optional(),
  barcode: z.string()
    .regex(/^[0-9]*$/, 'Barcode must contain only numbers')
    .min(8, 'Barcode must be at least 8 characters')
    .optional()
    .or(z.literal('')),
  category: z.string().optional(),
  cost: z.number({ error: 'Cost price is required' })
    .positive('Cost price must be greater than 0'),
  price: z.number({ error: 'Selling price is required' })
    .positive('Selling price must be greater than 0')
    .refine(val => !formData.value.cost || val >= formData.value.cost, {
      message: 'Selling price must be greater than or equal to cost'
    }),
  minQuantity: z.number()
    .min(0, 'Minimum quantity must be 0 or greater')
    .or(z.undefined()),
  maxQuantity: z.number()
    .min(1, 'Maximum quantity must be at least 1')
    .refine(val => !formData.value.minQuantity || val >= formData.value.minQuantity, {
      message: 'Maximum quantity must be greater than minimum quantity'
    })
    .or(z.undefined()),
  isActive: z.boolean()
})
  .refine(
    data => !data.maxQuantity || (data.minQuantity !== undefined && data.minQuantity <= data.maxQuantity),
    { message: "Maximum quantity must be greater than minimum quantity", path: ["maxQuantity"] }
  );

const isFormValid = computed(() => (
  !hasErrors.value &&
  formData.value.name.trim() !== '' &&
  formData.value.sku.trim() !== '' &&
  (formData.value.cost ?? 0) > 0 &&
  (formData.value.price ?? 0) > 0
));

const loadProduct = async () => {
  if (!isEditing.value) return;
  loading.value = true;
  try {
    await productStore.fetchProducts();
    const product = productStore.productById(productId.value);
    if (product) {
      formData.value = {
        name: product.name,
        sku: product.sku,
        description: product.description || '',
        barcode: product.barcode || '',
        category: product.category || '',
        cost: product.cost.amount,
        price: product.price.amount,
        minQuantity: product.minQuantity.value,
        maxQuantity: product.maxQuantity?.value,
        isActive: product.isActive
      };
      productCurrency.value = product.cost.currency;
      productUnit.value = product.minQuantity.unit;
    }
  } catch (err) {
    error.value = 'Failed to load product';
    console.error('Error loading product:', err);
  } finally {
    loading.value = false;
  }
};

const handleSubmit = async () => {
  isSubmitting.value = true;
  error.value = null;
  try {
    const isValid = await validateForm();
    if (!isValid) return;
    loading.value = true;
    const cleanFormData = {
      ...formData.value,
      description: formData.value.description?.trim() || '',
      barcode: formData.value.barcode?.trim() || '',
      category: formData.value.category?.trim() || '',
      cost: formData.value.cost!,
      price: formData.value.price!,
      minQuantity: formData.value.minQuantity || 0,
      maxQuantity: formData.value.maxQuantity
    };
    const productDTO = {
      name: cleanFormData.name.trim(),
      sku: cleanFormData.sku.trim().toUpperCase(),
      description: cleanFormData.description,
      barcode: cleanFormData.barcode,
      category: cleanFormData.category,
      isActive: cleanFormData.isActive,
      cost: { amount: cleanFormData.cost, currency: productCurrency.value },
      price: { amount: cleanFormData.price, currency: productCurrency.value },
      minQuantity: { value: cleanFormData.minQuantity, unit: productUnit.value },
      maxQuantity: cleanFormData.maxQuantity !== undefined
        ? { value: cleanFormData.maxQuantity, unit: productUnit.value }
        : undefined
    };
    if (isEditing.value) {
      await productStore.updateProduct(productId.value, productDTO);
    } else {
      await productStore.createProduct(productDTO);
    }
    router.push('/products');
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to save product';
    console.error('Error saving product:', err);
  } finally {
    loading.value = false;
    isSubmitting.value = false;
  }
};

const validateField = async (name: keyof typeof formData.value) => {
  try {
    const schema = formSchema.pick({ [name]: true });
    await schema.parseAsync({ [name]: formData.value[name] });
    errors.value[name] = [];
  } catch (error) {
    if (error instanceof z.ZodError) {
      errors.value[name] = error.issues.map(e => e.message);
    }
  }
};

const validateForm = async () => {
  try {
    await formSchema.parseAsync(formData.value);
    errors.value = {};
    return true;
  } catch (error) {
    if (error instanceof z.ZodError) {
      const newErrors: Record<string, string[]> = {};
      error.issues.forEach((err) => {
        const path = err.path[0] as string;
        if (!newErrors[path]) newErrors[path] = [];
        newErrors[path].push(err.message);
      });
      errors.value = newErrors;
    }
    return false;
  }
};

const handleBarcodeScanned = (barcode: string) => {
  formData.value.barcode = barcode;
  validateField('barcode');
};

const handleScannerError = (error: Error) => {
  console.error('Scanner error:', error);
};

const generateSKU = () => {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substr(2, 4);
  formData.value.sku = `SKU-${timestamp}-${random}`.toUpperCase();
};

const generateBarcode = () => {
  const barcode = '1' + Math.random().toString().substr(2, 11);
  formData.value.barcode = barcode;
};

const duplicateProduct = () => {
  formData.value.sku = formData.value.sku + '-COPY';
  formData.value.barcode = '';
};

const clearError = () => { error.value = null; };

onMounted(async () => {
  if (isEditing.value) await loadProduct();
});

watch(() => route.params.id, () => {
  if (isEditing.value) loadProduct();
});
</script>
